import React from "react";

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  className?: string;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "danger" | "success" | "outline";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  type = "button",
  className = "",
  disabled = false,
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
}) => {
  const baseStyles =
    "font-medium sm:font-semibold rounded-lg transition-all duration-200 inline-flex items-center justify-center btn-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2";

  const variants = {
    primary: "bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-400",
    secondary: "bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:bg-gray-100",
    danger: "bg-danger-600 text-white hover:bg-danger-700 disabled:bg-danger-400",
    success: "bg-success-600 text-white hover:bg-success-700 disabled:bg-success-500",
    outline:
      "border-2 border-primary-600 text-primary-600 hover:bg-primary-50 disabled:border-primary-300 disabled:text-primary-300",
  };

  const sizes = {
    sm: "px-2.5 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm",
    md: "px-4 py-2 text-sm sm:px-6 sm:py-2.5 sm:text-base",
    lg: "px-6 py-2.5 text-base sm:px-8 sm:py-3.5 sm:text-lg",
  };

  const loaderSizes = {
    sm: "w-2.5 h-2.5 sm:w-3 sm:h-3",
    md: "w-4 h-4 sm:w-5 sm:h-5",
    lg: "w-5 h-5 sm:w-6 sm:h-6",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? "w-full" : ""}
        ${disabled || loading ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
        ${className}
      `}
    >
      {loading ? (
        <div className="flex items-center py-1">
          <span className={`inline-block border-2 border-white border-t-transparent rounded-full animate-spin mr-2 ${loaderSizes[size]}`}></span>
        </div>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
